import React, { useCallback } from 'react';
import './MacAIChatWidget.css';

// ── Constants ────────────────────────────────────────────────────────────────
// Reject widget HTML shorter than this — prevents rendering empty / broken iframes
const MINIMUM_HTML_LENGTH = 100;

// ── Types ────────────────────────────────────────────────────────────────────
// Defined here (source of truth for the widget shape).
// chat.types.ts will add `widget?: MacAIWidgetData` to MessageMetadata in Task 4.
export interface MacAIWidgetData {
  /** e.g. "usecase_explorer" */
  type: string;
  /** Only 'srcdoc' is supported — others are rejected */
  render_type: 'srcdoc' | string;
  /** Full HTML document rendered as iframe srcdoc */
  html: string;
}

interface MacAIChatWidgetProps {
  widget: MacAIWidgetData;
  /** Called after the iframe finishes loading — used by parent to scroll into view */
  onLoad?: () => void;
}

// ── Component ────────────────────────────────────────────────────────────────
const MacAIChatWidget: React.FC<MacAIChatWidgetProps> = ({ widget, onLoad }) => {
  const handleLoad = useCallback(() => {
    onLoad?.();
  }, [onLoad]);

  // Guard: reject invalid / empty HTML
  if (!widget?.html || widget.html.length < MINIMUM_HTML_LENGTH) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('[MacAIChatWidget] Invalid or missing widget HTML', widget);
    }
    return null;
  }

  // Guard: only srcdoc rendering is supported
  if (widget.render_type !== 'srcdoc') {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[MacAIChatWidget] Unsupported render_type:', widget.render_type);
    }
    return null;
  }

  return (
    <div className="mac-widget-container">
      <iframe
        srcDoc={widget.html}
        title={widget.type}
        className="mac-widget-iframe"
        /**
         * sandbox="allow-scripts":
         * - Allows the widget JS to run (required for interactivity + postMessage)
         * - Denies same-origin access — iframe cannot read parent cookies/localStorage
         * - postMessage to parent still works ✅ (cross-origin messaging is unaffected by sandbox)
         */
        sandbox="allow-scripts"
        onLoad={handleLoad}
      />
    </div>
  );
};

export default React.memo(MacAIChatWidget);
